/* eslint-disable radar/cognitive-complexity */
import { useMutation, useQueryClient, InfiniteData } from 'react-query';

import { api } from '@services';
import { TimelineResponse, PostCommentsResponse } from '@common/types/api';

export const useDeleteCommentMutation = (postId: number, commentId: number) => {
  const queryClient = useQueryClient();
  const queryKey = ['post-comments', postId] as const;
  const postsQueryKey = ['newsfeed'] as const;

  return useMutation(() => api.delete(`/timeline/comment/${commentId}`), {
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);
      await queryClient.cancelQueries(postsQueryKey);

      const previousComments =
        queryClient.getQueryData<InfiniteData<PostCommentsResponse>>(queryKey);
      const previousPosts =
        queryClient.getQueryData<InfiniteData<TimelineResponse>>(postsQueryKey);

      if (previousComments) {
        const newPages = previousComments.pages.map((page) => ({
          ...page,
          data: page.data.filter((c) => c.id !== commentId),
        }));

        queryClient.setQueryData<InfiniteData<PostCommentsResponse>>(queryKey, {
          ...previousComments,
          pages: newPages,
        });
      }

      if (previousPosts) {
        queryClient.setQueryData<InfiniteData<TimelineResponse>>(
          postsQueryKey,
          {
            ...previousPosts,
            pages: previousPosts.pages.map((page) => {
              const postIndex = page.data.findIndex(
                (post) => post.id === postId,
              );

              if (postIndex < 0) {
                return page;
              }
              return {
                ...page,
                data: page.data.map((post, index) => {
                  if (postIndex !== index) {
                    return post;
                  }

                  return {
                    ...post,
                    comments_total: Math.max(0, post.comments_total - 1),
                  };
                }),
              };
            }),
          },
        );
      }
      return { previousComments, previousPosts };
    },
    onError: (_, __, ctx) => {
      if (ctx?.previousComments) {
        queryClient.setQueryData(queryKey, ctx.previousComments);
      }
      if (ctx?.previousPosts) {
        queryClient.setQueryData(postsQueryKey, ctx.previousPosts);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey);
      queryClient.invalidateQueries(postsQueryKey);
    },
  });
};
